import { Box, Typography, useTheme, Chip } from '@mui/material';
import { CodeOutlined } from '@mui/icons-material';
import SettingCard from './SettingCard';
import SettingRow from './SettingRow';
import WolfButton from '@components/ui/WolfButton';
import { useTranslation } from '@hooks/useTranslation';

interface DeveloperSettingsSectionProps {
  autoEnableMic: boolean;
  wakeWordEnabled: boolean;
  wolfieSpeaking: boolean;
  wolfieVideoMode: boolean;
  devPanelEnabled: boolean;
  onAutoEnableMicToggle: () => void;
  onWakeWordToggle: () => void;
  onVoiceModeToggle: () => void;
  onAvatarModeToggle: () => void;
  onDevPanelToggle: () => void;
  onManualOrgUpdate: (all: boolean) => Promise<void>;
}

const DeveloperSettingsSection: React.FC<DeveloperSettingsSectionProps> = ({
  autoEnableMic,
  wakeWordEnabled,
  wolfieSpeaking,
  wolfieVideoMode,
  devPanelEnabled,
  onAutoEnableMicToggle,
  onWakeWordToggle,
  onVoiceModeToggle,
  onAvatarModeToggle,
  onDevPanelToggle,
  onManualOrgUpdate,
}) => {
  const theme = useTheme();
  const localeSettings = useTranslation('settings');
  const localeCommon = useTranslation('common');

  return (
    <SettingCard
      title={localeSettings['developerSettings'] || 'Developer Settings'}
      icon={<CodeOutlined />}
      iconColor={theme.palette.feedbackWarning.main}>
      {/* Auto Enable Mic */}
      <SettingRow
        label={localeSettings['autoEnableMic'] + ':'}
        description={
          <Box className="flex items-center gap-2 mt-1">
            <Typography variant="body14" className="text-gray-600">
              {localeSettings['autoEnableMicDescription']}
            </Typography>
            <Chip
              label={!autoEnableMic ? localeCommon['disabled'] : localeCommon['enabled']}
              size="small"
              color={autoEnableMic ? 'success' : 'default'}
              variant="outlined"
            />
          </Box>
        }>
        <WolfButton color="primary" onClick={onAutoEnableMicToggle}>
          {!autoEnableMic ? localeCommon['enable'] : localeCommon['disable']}
        </WolfButton>
      </SettingRow>

      {/* Voice Activation (Wake Word) */}
      <SettingRow
        label={localeSettings['voiceActivation'] + ':'}
        description={
          <Box className="flex items-center gap-2 mt-1">
            <Typography variant="body14" className="text-gray-600">
              {localeSettings['voiceActivationDescription']}
            </Typography>
            <Chip
              label={!wakeWordEnabled ? localeCommon['disabled'] : localeCommon['enabled']}
              size="small"
              color={wakeWordEnabled ? 'success' : 'default'}
              variant="outlined"
            />
          </Box>
        }>
        <WolfButton color="primary" onClick={onWakeWordToggle}>
          {!wakeWordEnabled ? localeCommon['enable'] : localeCommon['disable']}
        </WolfButton>
      </SettingRow>

      {/* Wolfie Voice Mode */}
      <SettingRow
        label={(localeSettings['wolfieVoiceMode'] || localeSettings['voiceMode']) + ':'}
        description={
          <Box className="flex items-center gap-2 mt-1">
            <Typography variant="body14" className="text-gray-600">
              {localeSettings['voiceModeDescription']}
            </Typography>
            <Chip
              label={!wolfieSpeaking ? localeCommon['disabled'] : localeCommon['enabled']}
              size="small"
              color={wolfieSpeaking ? 'success' : 'default'}
              variant="outlined"
            />
          </Box>
        }>
        <WolfButton color="primary" onClick={onVoiceModeToggle}>
          {!wolfieSpeaking ? localeCommon['enable'] : localeCommon['disable']}
        </WolfButton>
      </SettingRow>

      {/* Wolfie Avatar Mode */}
      <SettingRow
        label={(localeSettings['wolfieAvatarMode'] || localeSettings['avatarMode']) + ':'}
        description={
          <Box className="flex items-center gap-2 mt-1">
            <Typography variant="body14" className="text-gray-600">
              {localeSettings['avatarModeDescription']}
            </Typography>
            <Chip
              label={!wolfieVideoMode ? localeCommon['disabled'] : localeCommon['enabled']}
              size="small"
              color={wolfieVideoMode ? 'success' : 'default'}
              variant="outlined"
            />
          </Box>
        }>
        <WolfButton color="primary" onClick={onAvatarModeToggle}>
          {!wolfieVideoMode ? localeCommon['enable'] : localeCommon['disable']}
        </WolfButton>
      </SettingRow>

      {/* Wolfie Dev Panel */}
      <SettingRow
        label={localeSettings['wolfieDevPanel'] + ':'}
        description={
          <Box className="flex items-center gap-2 mt-1">
            <Typography variant="body14" className="text-gray-600">
              {localeSettings['wolfieDevPanelDescription']}
            </Typography>
            <Chip
              label={!devPanelEnabled ? localeCommon['disabled'] : localeCommon['enabled']}
              size="small"
              color={devPanelEnabled ? 'success' : 'default'}
              variant="outlined"
            />
          </Box>
        }>
        <WolfButton color="primary" onClick={onDevPanelToggle}>
          {!devPanelEnabled ? localeCommon['enable'] : localeCommon['disable']}
        </WolfButton>
      </SettingRow>

      {/* Manual Org Update (Current) */}
      <SettingRow
        label={localeSettings['manualOrgUpdate'] + ':'}
        description={localeSettings['manualOrgUpdateDescription']}>
        <WolfButton color="primary" onClick={() => onManualOrgUpdate(false)}>
          {localeSettings['updateNow'] || 'Update Now'}
        </WolfButton>
      </SettingRow>

      {/* Manual Org Update (All Organizations) */}
      <SettingRow
        label={(localeSettings['manualOrgUpdateAll'] || 'Manual Update All Organizations') + ':'}
        description={localeSettings['manualOrgUpdateAllDescription']}>
        <WolfButton color="primary" onClick={() => onManualOrgUpdate(true)}>
          {localeSettings['updateAllNow'] || 'Update All Now'}
        </WolfButton>
      </SettingRow>
    </SettingCard>
  );
};

export default DeveloperSettingsSection;
